import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { NavLink, Link, useLocation } from "react-router-dom";
import { TwitchLogo, GlobeHemisphereWest } from "@phosphor-icons/react";
import { Menu, X } from "lucide-react";
import { LiveBadge } from "./LiveBadge";
import { SOCIALS } from "../config/socials";
import { OOL_LOGO_URL, DOMAIN_PARTS } from "../config/brand";
import { useCountry } from "../hooks/useCountry";

const links = [
    { label: "Home", to: "/" },
    { label: "Live", to: "/live" },
    { label: "Schedule", to: "/schedule" },
    { label: "Clips", to: "/clips" },
    { label: "About", to: "/about" },
    { label: "Community", to: "/community" },
    { label: "Contact", to: "/contact" },
];

const openCountryPicker = () => {
    window.dispatchEvent(new CustomEvent("ool:open-country-picker"));
};

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { country } = useCountry();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <motion.header
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? "glass-strong" : "bg-black/30 backdrop-blur-md"
            } border-b border-white/[0.05]`}
            data-testid="main-navbar"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
                    <motion.div
                        whileHover={{ rotate: 360, scale: 1.08 }}
                        transition={{ type: "spring", stiffness: 120, damping: 14 }}
                        className="relative w-9 h-9 rounded-full overflow-visible flex items-center justify-center"
                        data-testid="nav-logo-mark"
                        style={{ willChange: "transform" }}
                    >
                        {/* GPU-friendly pulsing halo: only opacity + transform animate, no box-shadow */}
                        <motion.span
                            aria-hidden
                            className="absolute inset-0 rounded-full bg-cyan-400 blur-md pointer-events-none"
                            animate={{ opacity: [0.35, 0.7, 0.35], scale: [1, 1.18, 1] }}
                            transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
                            style={{ willChange: "transform, opacity" }}
                        />
                        <span
                            className="absolute inset-0 rounded-full bg-black ring-2 ring-brand-cyan/70"
                            aria-hidden
                            style={{ boxShadow: "0 0 14px rgba(34, 211, 238, 0.55)" }}
                        />
                        <img src={OOL_LOGO_URL} alt="ools.me logo" className="relative w-full h-full object-cover rounded-full" />
                    </motion.div>
                    <div className="flex flex-col leading-none">
                        <span className="font-display font-bold tracking-tight text-base flex items-baseline">
                            <span
                                className="text-brand-cyan"
                                style={{ textShadow: "0 0 14px rgba(34, 211, 238, 0.7)" }}
                                data-testid="nav-logo-name"
                            >
                                {DOMAIN_PARTS.name}
                            </span>
                            <span
                                className="text-white/85"
                                style={{ textShadow: "0 0 8px rgba(255,255,255,0.25)" }}
                                data-testid="nav-logo-tld"
                            >
                                {DOMAIN_PARTS.tld}
                            </span>
                        </span>
                        <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/45 mt-0.5" data-testid="nav-logo-tagline">
                            itz_ool · daily streams
                        </span>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            className={({ isActive }) =>
                                `relative px-3.5 py-2 text-sm transition-colors ${
                                    isActive ? "text-white" : "text-white/55 hover:text-white"
                                }`
                            }
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                        >
                            {({ isActive }) => (
                                <>
                                    {l.label}
                                    {isActive && (
                                        <motion.span
                                            layoutId="nav-underline"
                                            className="absolute left-3.5 right-3.5 -bottom-px h-px bg-brand-cyan"
                                            transition={{ type: "spring", stiffness: 380, damping: 30 }}
                                        />
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    {/* Country switcher */}
                    <button
                        onClick={openCountryPicker}
                        className="hidden md:inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/75 text-xs hover:border-white/30 hover:text-white transition"
                        data-testid="nav-country-btn"
                        aria-label="Change country"
                    >
                        {country ? (
                            <>
                                <span className="leading-none text-base">{country.flag}</span>
                                <span className="font-mono text-[11px]">{country.code}</span>
                            </>
                        ) : (
                            <>
                                <GlobeHemisphereWest size={13} weight="bold" />
                                <span>Pick country</span>
                            </>
                        )}
                    </button>
                    <div className="hidden md:block" data-testid="live-indicator-nav">
                        <LiveBadge size="sm" />
                    </div>
                    <a
                        href={SOCIALS.twitch.url}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-sweep hidden sm:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white text-black text-xs font-semibold hover:bg-brand-cyan transition-colors"
                        data-testid="nav-cta-twitch"
                    >
                        <TwitchLogo size={13} weight="fill" />
                        Follow
                    </a>
                    <button
                        className="lg:hidden p-2 rounded-lg bg-white/5 border border-white/10"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={16} /> : <Menu size={16} />}
                    </button>
                </div>
            </div>

            {open && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    className="lg:hidden glass-strong border-t border-white/10 px-6 py-3 flex flex-col gap-0.5"
                    data-testid="mobile-menu"
                >
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            className={({ isActive }) =>
                                `py-2.5 px-3 rounded-lg text-sm ${
                                    isActive ? "text-brand-cyan bg-white/5" : "text-white/75 hover:text-white"
                                }`
                            }
                            data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <button
                        onClick={openCountryPicker}
                        className="mt-2 py-2.5 px-3 rounded-lg text-sm text-white/75 hover:text-white text-left flex items-center gap-2"
                        data-testid="mobile-country-btn"
                    >
                        {country ? (
                            <>
                                <span className="text-base leading-none">{country.flag}</span>
                                <span>{country.name}</span>
                            </>
                        ) : (
                            <>
                                <GlobeHemisphereWest size={14} weight="bold" />
                                <span>Pick country</span>
                            </>
                        )}
                    </button>
                </motion.div>
            )}
        </motion.header>
    );
};
